/**
 * Zebra Browser Print Utility
 * 
 * This module provides functions to interact with Zebra printers
 * using the Browser Print library for direct ZPL printing.
 * 
 * Requirements:
 * - Zebra Browser Print application must be installed on local machine
 * - BrowserPrint library must be loaded in index.html
 */

/**
 * Wait for BrowserPrint library to be loaded
 * @param {number} timeout - Maximum time to wait in milliseconds
 * @returns {Promise<void>}
 */
const waitForBrowserPrint = (timeout = 5000) => {
    return new Promise((resolve, reject) => {
        // Check if already loaded s
        if (typeof window.BrowserPrint !== 'undefined') {
            resolve()
            return
        }

        // Poll for library availability
        const startTime = Date.now()
        const checkInterval = setInterval(() => {
            if (typeof window.BrowserPrint !== 'undefined') {
                clearInterval(checkInterval)
                resolve()
            } else if (Date.now() - startTime > timeout) {
                clearInterval(checkInterval)
                reject(new Error('Zebra Browser Print library failed to load. Please ensure the Browser Print application is installed and running.'))
            }
        }, 100)
    })
}

/**
 * Get the default Zebra printer connected to the system
 * @returns {Promise<Object>} - Returns the default printer object
 */
export const getDefaultPrinter = async () => {
    // Wait for BrowserPrint to be available
    await waitForBrowserPrint()

    return new Promise((resolve, reject) => {
        window.BrowserPrint.getDefaultDevice('printer', (device) => {
            if (device) {
                resolve(device)
            } else {
                reject(new Error('No default printer found. Please ensure a Zebra printer is connected and configured as default.'))
            }
        }, (error) => {
            reject(new Error(`Failed to get default printer: ${error?.message || error}`))
        })
    })
}

/**
 * Get list of all Zebra printers connected to the system
 * @returns {Promise<Array>} - Returns array of printer objects
 */
export const getLocalPrinters = async () => {
    // Wait for BrowserPrint to be available
    await waitForBrowserPrint()

    return new Promise((resolve, reject) => {
        window.BrowserPrint.getLocalDevices((devices) => {
            if (devices && devices.length > 0) {
                // Filter only printer devices
                const printers = devices.filter(device => device.deviceType === 'printer')
                resolve(printers)
            } else {
                resolve([])
            }
        }, (error) => {
            reject(new Error(`Failed to get local printers: ${error?.message || error}`))
        })
    })
}

/**
 * Send ZPL data to specified Zebra printer
 * @param {Object} printer - Printer object from getDefaultPrinter or getLocalPrinters
 * @param {string} zplData - ZPL formatted string to print
 * @returns {Promise<void>}
 */
export const sendZplToPrinter = (printer, zplData) => {
    return new Promise((resolve, reject) => {
        if (!printer) {
            reject(new Error('No printer specified'))
            return
        }

        if (!zplData || typeof zplData !== 'string') {
            reject(new Error('Invalid ZPL data'))
            return
        }

        // Send ZPL data to printer
        printer.send(zplData, 
            () => {
                resolve()
            },
            (error) => {
                reject(new Error(`Failed to print: ${error}`))
            }
        )
    })
}

/**
 * Check if Browser Print is available
 * @param {boolean} waitForLoad - Whether to wait for library to load
 * @returns {Promise<boolean>|boolean}
 */
export const isBrowserPrintAvailable = (waitForLoad = false) => {
    if (!waitForLoad) {
        return typeof window.BrowserPrint !== 'undefined'
    }
    
    // Return promise that waits for library
    return waitForBrowserPrint(3000)
        .then(() => true)
        .catch(() => false)
}

/**
 * Print luggage tag using default printer
 * @param {string} zplData - ZPL formatted string from API
 * @returns {Promise<void>}
 */
export const printLuggageTag = async (zplData) => {
    // Wait for BrowserPrint to be available
    await waitForBrowserPrint()
    
    const printer = await getDefaultPrinter()
    await sendZplToPrinter(printer, zplData)
}

/**
 * Print luggage tag with printer selection
 * @param {string} zplData - ZPL formatted string from API
 * @param {Object} selectedPrinter - Optional specific printer to use
 * @returns {Promise<void>}
 */
export const printLuggageTagWithPrinter = async (zplData, selectedPrinter = null) => {
    // Wait for BrowserPrint to be available
    await waitForBrowserPrint()
    
    const printer = selectedPrinter || await getDefaultPrinter()
    await sendZplToPrinter(printer, zplData)
}
